import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import type {
  AppCategorySummary,
  AppSummary,
} from "@/features/app-studio/model/appsSlice";
import {
  deleteApp,
  isAppSummary,
  parseAppCategoriesResponse,
  parseAppsResponse,
} from "@/features/app-studio/model/appsSlice";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type PublishedAppsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  apps: AppSummary[];
  categories: AppCategorySummary[];
  categoriesStatus: "idle" | "loading" | "succeeded" | "failed";
  detailStatus: "idle" | "loading" | "succeeded" | "failed";
  detailApp: AppSummary | null;
  detailError: string | null;
  /**
   * Every app detail fetched this session, by id. `detailApp` alone holds one app, so a favourites
   * prefetch warming several would leave all but the last of them with nothing to show. Keyed
   * entries let each app's view paint from its own already-fetched detail while the refetch runs.
   */
  detailAppsById: Record<number, AppSummary>;
  togglingFavoriteAppIds: number[];
};

export const publishedAppsInitialState: PublishedAppsState = {
  status: STATUS.IDLE,
  apps: [],
  categories: [],
  categoriesStatus: STATUS.IDLE,
  detailStatus: STATUS.IDLE,
  detailApp: null,
  detailError: null,
  detailAppsById: {},
  togglingFavoriteAppIds: [],
};

export const fetchPublishedApps = createAsyncThunk<
  AppSummary[],
  void,
  { rejectValue: string }
>(
  "publishedApps/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/published-apps", { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(`Request failed (${res.status})`);
      }
      const data: unknown = await res.json();
      const apps = parseAppsResponse(data);
      if (!apps) {
        return rejectWithValue("Unexpected response shape");
      }
      return apps;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { status } = (getState() as { publishedApps: PublishedAppsState }).publishedApps;
      return status !== STATUS.LOADING && status !== STATUS.SUCCEEDED;
    },
  },
);

export const fetchPublishedAppCategories = createAsyncThunk<
  AppCategorySummary[],
  void,
  { rejectValue: string }
>(
  "publishedApps/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/published-apps/categories", { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(`Request failed (${res.status})`);
      }
      const data: unknown = await res.json();
      const categories = parseAppCategoriesResponse(data);
      if (!categories) {
        return rejectWithValue("Unexpected response shape");
      }
      return categories;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { categories, categoriesStatus } = (
        getState() as { publishedApps: PublishedAppsState }
      ).publishedApps;
      if (categories.length > 0) {
        return false;
      }
      return (
        categoriesStatus !== STATUS.LOADING && categoriesStatus !== STATUS.SUCCEEDED
      );
    },
  },
);

export type ToggleAppFavoritePayload = {
  appId: number;
  nextIsFavorite: boolean;
};

export const toggleAppFavorite = createAsyncThunk<
  ToggleAppFavoritePayload,
  ToggleAppFavoritePayload,
  { rejectValue: string }
>(
  "publishedApps/toggleFavorite",
  async ({ appId, nextIsFavorite }, { rejectWithValue }) => {
    try {
      const method = nextIsFavorite ? "PUT" : "DELETE";
      const res = await apiFetch(`/v1/published-apps/${appId}/favorite`, { method });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      return { appId, nextIsFavorite };
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
  {
    condition: ({ appId }, { getState }) => {
      const { togglingFavoriteAppIds } = (getState() as { publishedApps: PublishedAppsState })
        .publishedApps;
      return !togglingFavoriteAppIds.includes(appId);
    },
  },
);

export const fetchPublishedAppById = createAsyncThunk<
  AppSummary,
  number,
  { rejectValue: string }
>("publishedApps/fetchById", async (id, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/published-apps/${id}`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isAppSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

function setAppFavorite(state: PublishedAppsState, appId: number, isFavorite: boolean) {
  const app = state.apps.find((row) => row.id === appId);
  if (app) {
    app.is_favorite = isFavorite;
  }
  if (state.detailApp?.id === appId) {
    state.detailApp.is_favorite = isFavorite;
  }
  const cachedDetail = state.detailAppsById[appId];
  if (cachedDetail) {
    cachedDetail.is_favorite = isFavorite;
  }
}

const publishedAppsSlice = createSlice({
  name: "publishedApps",
  initialState: publishedAppsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchPublishedApps.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(fetchPublishedApps.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.apps = action.payload;
      })
      .addCase(fetchPublishedApps.rejected, (state) => {
        state.status = STATUS.FAILED;
      })
      .addCase(fetchPublishedAppCategories.pending, (state) => {
        state.categoriesStatus = STATUS.LOADING;
      })
      .addCase(fetchPublishedAppCategories.fulfilled, (state, action) => {
        state.categoriesStatus = STATUS.SUCCEEDED;
        state.categories = action.payload;
      })
      .addCase(fetchPublishedAppCategories.rejected, (state) => {
        state.categoriesStatus = STATUS.FAILED;
      })
      .addCase(fetchPublishedAppById.pending, (state) => {
        state.detailStatus = STATUS.LOADING;
        state.detailError = null;
      })
      .addCase(fetchPublishedAppById.fulfilled, (state, action) => {
        state.detailStatus = STATUS.SUCCEEDED;
        state.detailApp = action.payload;
        state.detailAppsById[action.payload.id] = action.payload;
      })
      .addCase(fetchPublishedAppById.rejected, (state, action) => {
        state.detailStatus = STATUS.FAILED;
        state.detailApp = null;
        state.detailError = action.payload ?? "Request failed";
        // Drop the cached copy too: an app that has since been unpublished or deleted must surface
        // the error rather than keep rendering from what was fetched before it went away.
        delete state.detailAppsById[action.meta.arg];
      })
      .addCase(toggleAppFavorite.pending, (state, action) => {
        const { appId, nextIsFavorite } = action.meta.arg;
        if (!state.togglingFavoriteAppIds.includes(appId)) {
          state.togglingFavoriteAppIds.push(appId);
        }
        setAppFavorite(state, appId, nextIsFavorite);
      })
      .addCase(toggleAppFavorite.fulfilled, (state, action) => {
        state.togglingFavoriteAppIds = state.togglingFavoriteAppIds.filter(
          (id) => id !== action.payload.appId,
        );
      })
      .addCase(toggleAppFavorite.rejected, (state, action) => {
        const { appId, nextIsFavorite } = action.meta.arg;
        state.togglingFavoriteAppIds = state.togglingFavoriteAppIds.filter(
          (id) => id !== appId,
        );
        setAppFavorite(state, appId, !nextIsFavorite);
      })
      .addCase(deleteApp.fulfilled, (state, action) => {
        state.apps = state.apps.filter((app) => app.id !== action.payload);
        delete state.detailAppsById[action.payload];
        if (state.detailApp?.id === action.payload) {
          state.detailApp = null;
          state.detailStatus = STATUS.IDLE;
          state.detailError = null;
        }
      });
  },
});

export const selectTogglingFavoriteAppIds = (state: {
  publishedApps: PublishedAppsState;
}) => state.publishedApps.togglingFavoriteAppIds;

export const publishedAppsReducer = publishedAppsSlice.reducer;
